import { mdiSubdirectoryArrowRight } from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React from 'react';
import { DatePicker } from '../../base';
import { formatDateForBackend } from '../../../services/primitives/date-utils';
import { DateOfServiceMatchValue } from '../../../services/worker-configuration/automation/worker-automation.types';
import { WorkerStore } from '../../../stores/worker-configuration/worker.store';
import * as S from './configuration-rule.styles';

interface IConfigurationRuleDosEditorProps {
    disabled?: boolean;
    matchIndex: number;
    ruleId: string;
    store: WorkerStore;
    value: DateOfServiceMatchValue;
}

function isValidIsoDate(value: string | null): boolean {
    if (!value) {
        return true;
    }

    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return false;
    }

    const [year, month, day] = value.split('-').map(Number);
    const parsed = new Date(year, month - 1, day);

    // Compare local calendar parts — avoid toISOString() (UTC shift breaks UTC+ timezones).
    return (
        parsed.getFullYear() === year &&
        parsed.getMonth() === month - 1 &&
        parsed.getDate() === day
    );
}

export const ConfigurationRuleDosEditor: React.FC<IConfigurationRuleDosEditorProps> = observer(
    ({ disabled, matchIndex, ruleId, store, value }) => {
        const fromDos = value.fromDos ?? '';
        const throughDos = value.throughDos ?? '';
        const hasAny = Boolean(fromDos || throughDos);
        const fromValid = isValidIsoDate(value.fromDos);
        const throughValid = isValidIsoDate(value.throughDos);
        const rangeValid =
            !fromDos || !throughDos || !fromValid || !throughValid || fromDos <= throughDos;

        const hint = !hasAny
            ? 'Set at least one date. Either side may be blank.'
            : !fromValid || !throughValid
              ? 'Use ISO dates (YYYY-MM-DD). Malformed dates will not match.'
              : !rangeValid
                ? 'From date must be on or before through date.'
                : fromDos && !throughDos
                  ? 'Matches service lines on or after this date.'
                  : !fromDos && throughDos
                    ? 'Matches service lines on or before this date.'
                    : 'Matches service lines within this inclusive range.';

        const warning = !hasAny || !fromValid || !throughValid || !rangeValid;

        return (
            <S.DosEditor>
                <S.PayerSectionHint>
                    <Icon path={mdiSubdirectoryArrowRight} size={0.75} color="#8e9293" aria-hidden={true} />
                    Date of service range
                </S.PayerSectionHint>

                <S.DosFields>
                    <S.DosField>
                        <S.FieldLabel>From</S.FieldLabel>
                        <DatePicker
                            disabled={disabled}
                            value={fromDos || undefined}
                            format="yyyy-MM-dd"
                            errorMessage={!fromValid || !rangeValid ? 'Invalid date' : undefined}
                            inputProps={{
                                placeholder: 'YYYY-MM-DD',
                            }}
                            onDateSelected={date => {
                                store.setDateOfServiceRange(ruleId, matchIndex, {
                                    fromDos: date ? formatDateForBackend(date) : null,
                                });
                            }}
                        />
                    </S.DosField>

                    <S.DosRangeArrow aria-hidden={true}>→</S.DosRangeArrow>

                    <S.DosField>
                        <S.FieldLabel>Through</S.FieldLabel>
                        <DatePicker
                            disabled={disabled}
                            value={throughDos || undefined}
                            format="yyyy-MM-dd"
                            errorMessage={!throughValid || !rangeValid ? 'Invalid date' : undefined}
                            inputProps={{
                                placeholder: 'YYYY-MM-DD',
                            }}
                            onDateSelected={date => {
                                store.setDateOfServiceRange(ruleId, matchIndex, {
                                    throughDos: date ? formatDateForBackend(date) : null,
                                });
                            }}
                        />
                    </S.DosField>
                </S.DosFields>

                <S.DosHint tone={warning ? 'warn' : 'muted'}>{hint}</S.DosHint>
            </S.DosEditor>
        );
    },
);
